import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import locationsData from '../../data/locations.json'

const LocationContext = createContext(null)

/**
 * LocationProvider Component
 * Provides location state and navigation functions throughout the app
 */
export function LocationProvider({ children }) {
  const [currentLocation, setCurrentLocation] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const previousLocationRef = useRef(null)
  const transitionTimeoutRef = useRef(null)

  const navigateTo = useCallback((locationId) => {
    if (isTransitioning) {
      console.warn('Location transition already in progress')
      return false
    }

    const location = locationsData.locations.find(loc => loc.id === locationId)
    
    if (!location) {
      console.warn(`Location not found: ${locationId}`)
      return false
    }

    setIsTransitioning(true)
    previousLocationRef.current = currentLocation
    setCurrentLocation(location)

    // Clear any existing timeout
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current)
    }

    // Reset transitioning state after animation completes
    const duration = location.camera?.transitionDuration || 2.0
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false)
      transitionTimeoutRef.current = null
    }, duration * 1000)

    return true
  }, [currentLocation, isTransitioning])

  const navigateToDefault = useCallback(() => {
    if (isTransitioning) {
      console.warn('Location transition already in progress')
      return false
    }

    setIsTransitioning(true)
    previousLocationRef.current = currentLocation
    setCurrentLocation(null)

    // Clear any existing timeout
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current)
    }

    // Reset after default transition (2 seconds)
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false)
      transitionTimeoutRef.current = null
    }, 2000)

    return true
  }, [currentLocation, isTransitioning])

  const getLocation = useCallback((locationId) => {
    return locationsData.locations.find(loc => loc.id === locationId)
  }, [])

  const getAllLocations = useCallback(() => {
    return locationsData.locations
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current !== null) {
        clearTimeout(transitionTimeoutRef.current)
        transitionTimeoutRef.current = null
      }
    }
  }, [])

  const value = {
    currentLocation,
    previousLocation: previousLocationRef.current,
    isTransitioning,
    navigateTo,
    navigateToDefault,
    getLocation,
    getAllLocations
  }

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

/**
 * useLocation hook
 * Access location state and navigation functions
 */
export function useLocation() {
  const context = useContext(LocationContext)
  
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider')
  }
  
  return context
}